import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useLocalizedState } from '@treams/treams-hooks/useLocalizedState';
import * as graphql from '@treams/treams-network';

import { Button } from '../common/button/Button';
import { IconButton } from '../common/button/IconButton';
import { Highlight } from '../common/Highlight';
import { PlainInput } from '../common/PlainInput';
import { Spinner } from '../common/Spinner';
import { Subheading } from '../common/Text';
import { Tooltip } from '../common/Tooltip';
import { WorkingArea } from '../common/WorkingArea';
import { ArchiveIcon, PencilEditBoxIcon, ReorderIcon } from '../icons';
import { tokens } from '../theme/tokens';

const Title = styled(Flex)<{ dragging?: boolean }>`
	align-items: center;
	gap: ${tokens.gap.inline}px;
	border-radius: ${tokens.radius.sm}px;

	${(props) =>
		!props.dragging &&
		`
        cursor: grab;

        &:hover {
            background: ${tokens.surface.hover};

            &:active {
                background: ${tokens.surface.secondary};
            }
        }
    `}
`;

const Handle = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	width: 32px;
	height: 32px;
	color: ${tokens.icon.tertiary};

	svg {
		display: block;
		width: 16px;
		height: 16px;
		color: inherit;
	}
`;

export function LibraryCategoryTitle(props: {
	active?: boolean;
	query?: string;
	name: graphql.LocalizedString;
	isArchived: boolean;
	loading?: boolean;
	dragging?: boolean;
	listeners?: object;
	onUpdate?: (name: graphql.LocalizedString) => Promise<void>;
	onArchive?: () => Promise<void>;
	onRestore?: () => Promise<void>;
}) {
	const { t } = useTranslation();

	const [editing, setEditing] = useState(false);
	const [name, setName] = useState(props.name);
	const [localizedName, setLocalizedName] = useLocalizedState(name, setName);

	const loading = props.loading || (props.dragging && !props.active);

	return (
		<Flex mb={3} px={2} alignItems="center">
			{props.isArchived || !props.onUpdate ? (
				<Subheading px={2}>
					<Highlight query={props.query}>{localizedName ?? ''}</Highlight>
				</Subheading>
			) : (
				<Title
					px={`${tokens.padding.control}px`}
					py={`${tokens.padding.compact}px`}
					my={`-${tokens.padding.compact}px`}
					dragging={props.dragging}
					{...(loading ? {} : props.listeners)}
				>
					<Handle>{loading ? <Spinner /> : <ReorderIcon size={16} />}</Handle>
					<WorkingArea
						flexible
						active={editing}
						onCancel={() => {
							setName(props.name);
							setEditing(false);
						}}
						onSave={async () => {
							await props.onUpdate?.(name);

							setEditing(false);
						}}
					>
						{(active) =>
							active ? (
								<Subheading
									style={{ background: tokens.surface.white, borderRadius: tokens.radius.md }}
									p={2}
									ml={-2}
									mt={-2}
								>
									<PlainInput
										value={localizedName}
										onChange={setLocalizedName}
										autoFocus
										multiline={false}
										onFocus={() => {
											setTimeout(() => {
												document.execCommand('selectAll', false, undefined);
											});
										}}
									/>
								</Subheading>
							) : (
								<Subheading>
									<Highlight query={props.query}>{localizedName ?? ''}</Highlight>
								</Subheading>
							)
						}
					</WorkingArea>
				</Title>
			)}
			{!props.isArchived && props.onUpdate && (
				<Tooltip content={t('settings.feedbackLibrary.category.edit.button')} portal>
					<IconButton
						type="plain"
						icon={<PencilEditBoxIcon />}
						ml={2}
						onClick={() => setEditing(true)}
					/>
				</Tooltip>
			)}
			<Box flexGrow={1} />
			{props.isArchived
				? props.onRestore && (
						<Button
							type="destructive"
							small
							icon={<ArchiveIcon />}
							title={t('settings.feedbackLibrary.category.restore.button')}
							ml={2}
							onClick={async () => {
								await props.onRestore?.();
							}}
						/>
					)
				: props.onArchive && (
						<Button
							type="destructive"
							small
							icon={<ArchiveIcon />}
							title={t('settings.feedbackLibrary.category.archive.button')}
							ml={2}
							onClick={async () => {
								await props.onArchive?.();
							}}
						/>
					)}
		</Flex>
	);
}
